import { getSessionUuid, getTokenLocalStorage, getTokenSessionStorage } from '@config/storage';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import React, { useState } from 'react';
import CustomModal from '../CustomModal';
import WolfInput from '../ui/WolfInput';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');
  const localeSettings = useTranslation('settings');
  const localeActionMessages = useTranslation('actionMessages');
  const localeLogin = useTranslation('login');

  const { rememberMe } = useAppSelector((state: RootState) => state.user);
  const { organizationId } = useAppSelector((state: RootState) => state.organization);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((show) => !show);
  };

  const resetForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setShowPassword(false);
  };

  const handleClose = () => {
    if (!saving) {
      resetForm();
      onClose();
    }
  };

  const handleUpdatePassword = async () => {
    // Validation
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      dispatch(
        appendActionMessage({
          message: localeLogin['allFieldsRequired'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    if (newPassword !== confirmNewPassword) {
      dispatch(
        appendActionMessage({
          message: localeLogin['passwordsDoNotMatch'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    setSaving(true);

    try {
      const url =
        (process.env.REACT_APP_BACKEND_URL + 'auth/' || process.env.REACT_APP_PUBLIC_AUTH_URL) + 'updatePassword';
      const payload = {
        previousPassword: oldPassword,
        newPassword,
      };
      const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
      const sessionUuid = getSessionUuid();

      if (url && token) {
        const auth = 'Bearer ' + token;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: auth,
            'Session-Uuid': sessionUuid,
            'X-Organization-Id': organizationId || ''
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (data.confirmation && data.confirmation === '200') {
          dispatch(
            appendActionMessage({
              message: localeActionMessages['passwordUpdatedSuccessfully'],
              type: SnackType.SUCCESS,
            }),
          );
          resetForm();
          onClose();
        } else {
          throw new Error(data.errorMessage || data.error || 'Failed to update password');
        }
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomModal
      title={localeSettings['changePassword']}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleUpdatePassword}
      showButtons={true}
      showConfirmButton={true}
      customConfirmText={saving ? `${localeCommon['saving']}...` : localeSettings['updatePassword']}
      customConfirmColor="primary"
      maxWidth="600px"
      rounded={true}>
      <Box className="p-4">
        <Box className="flex flex-col gap-4">
          <WolfInput
            label={localeSettings['oldPassword']}
            placeholder={localeSettings['oldPassword']}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            type={showPassword ? 'text' : 'password'}
            showIcon={true}
            icon={showPassword ? <VisibilityOff /> : <Visibility />}
            iconAction={handleTogglePasswordVisibility}
            boxFullWidth={true}
            disabled={saving}
          />
          <WolfInput
            label={localeSettings['newPassword']}
            placeholder={localeSettings['newPassword']}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            type={showPassword ? 'text' : 'password'}
            showIcon={true}
            icon={showPassword ? <VisibilityOff /> : <Visibility />}
            iconAction={handleTogglePasswordVisibility}
            boxFullWidth={true}
            disabled={saving}
          />
          <WolfInput
            label={localeSettings['confirmNewPassword']}
            placeholder={localeSettings['confirmNewPassword']}
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            showIcon={true}
            icon={showPassword ? <VisibilityOff /> : <Visibility />}
            iconAction={handleTogglePasswordVisibility}
            type={showPassword ? 'text' : 'password'}
            boxFullWidth={true}
            disabled={saving}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default ChangePasswordModal;
